import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Chart, ChartConfiguration, ChartData, Plugin } from 'chart.js';
import { TempDataPoint, SolarHistoryPoint } from '../version.service';

const crosshairPlugin: Plugin<'line'> = {
  id: 'crosshairLine',
  afterDraw(chart) {
    const active = (chart as any).tooltip?._active;
    if (!active?.length) return;
    const x    = active[0].element.x;
    const { ctx, chartArea } = chart;
    if (!chartArea) return;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x, chartArea.top);
    ctx.lineTo(x, chartArea.bottom);
    ctx.lineWidth    = 1;
    ctx.strokeStyle  = 'rgba(128,128,128,0.5)';
    ctx.setLineDash([4, 3]);
    ctx.stroke();
    ctx.restore();
  },
};
Chart.register(crosshairPlugin);

@Component({
  selector: 'app-sparkline-chart',
  template: `
    <div [style.height]="expanded ? 'auto' : '44px'"
         [style.aspect-ratio]="expanded ? '16/9' : null"
         style="position:relative;">
      <canvas baseChart
        [data]="chartData"
        [options]="chartOptions"
        type="line">
      </canvas>
    </div>
  `,
})
export class SparklineChartComponent implements OnChanges {
  @Input() points: TempDataPoint[] = [];
  @Input() darkMode = false;
  @Input() expanded = false;
  @Input() mode: 'temp' | 'humidity' = 'temp';

  @Input() solarPoints: SolarHistoryPoint[] = [];
  @Input() solarMode: 'pvYield' | 'battery' | 'grid' | 'consumption' | null = null;

  chartData: ChartData<'line'> = { datasets: [] };
  chartOptions: ChartConfiguration<'line'>['options'] = {};

  private fmtW(w: number): string {
    const abs = Math.abs(w);
    if (abs >= 1000) return (w / 1000).toFixed(1) + ' kW';
    return w.toFixed(0) + ' W';
  }

  ngOnChanges(_changes: SimpleChanges): void {
    const textColor  = this.darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)';
    const gridColor  = this.darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';

    if (this.solarMode !== null) {
      const solarColors: Record<string, string> = {
        pvYield:     '#ffc107',
        battery:     '#198754',
        grid:        '#0dcaf0',
        consumption: '#0d6efd',
      };
      const getValue = (p: SolarHistoryPoint): number => {
        switch (this.solarMode) {
          case 'pvYield':     return p.pvYieldW;
          case 'battery':     return -p.batteryDischargeW;
          case 'grid':        return p.gridConsumptionW;
          case 'consumption': return p.totalConsumptionW;
          default:            return 0;
        }
      };
      const solarYLabel: Record<string, string> = {
        pvYield:     'Erzeugung (W)',
        battery:     'Batterie (W)',
        grid:        'Netzbezug (W)',
        consumption: 'Verbrauch (W)',
      };
      this.chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              title: (items) => {
                const ts = items[0]?.parsed.x;
                return ts != null
                  ? new Date(ts).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
                  : '';
              },
              label: (item) => ` ${this.fmtW(item.parsed.y ?? 0)}`,
            },
          },
        },
        scales: {
          x: {
            display: this.expanded,
            type: 'linear',
            min: Date.now() - 24 * 3600 * 1000,
            max: Date.now(),
            ticks: {
              color: textColor,
              maxTicksLimit: 3,
              callback: (val) => {
                const diff = Date.now() - (val as number);
                const h24  = 24 * 3600 * 1000;
                const h12  = 12 * 3600 * 1000;
                if (diff < 5 * 60 * 1000)                    return 'Jetzt';
                if (Math.abs(diff - h12) < 30 * 60 * 1000)  return '–12h';
                if (Math.abs(diff - h24) < 30 * 60 * 1000)  return '–24h';
                return null;
              },
            },
            grid: { display: this.expanded, color: gridColor },
            title: { display: this.expanded, text: 'Zeit (24h)', color: textColor },
          },
          y: {
            display: true,
            ticks: {
              color: textColor,
              maxTicksLimit: this.expanded ? 6 : 3,
              callback: (val) => this.fmtW(val as number),
            },
            grid: { color: gridColor, display: this.expanded },
            title: {
              display: this.expanded,
              text: this.solarMode ? solarYLabel[this.solarMode] ?? 'Watt (W)' : 'Watt (W)',
              color: textColor,
            },
          },
        },
        elements: {
          point: { radius: 0, hoverRadius: 4 },
          line:  { borderWidth: this.expanded ? 2 : 1.5, tension: 0.4 },
        },
      };
      this.chartData = {
        datasets: [{
          data: this.solarPoints.map(p => ({ x: p.ts, y: getValue(p) })),
          borderColor: solarColors[this.solarMode] ?? '#6c757d',
          backgroundColor: 'transparent',
          fill: false,
        }],
      };
      return;
    }

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => {
              const ts = items[0]?.parsed.x;
              return ts != null
                ? new Date(ts).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
                : '';
            },
            label: (item) => this.mode === 'humidity'
              ? ` ${(item.parsed.y ?? 0).toFixed(0)} %`
              : ` ${(item.parsed.y ?? 0).toFixed(1)} °C`,
          },
        },
      },
      scales: {
        x: {
          display: this.expanded,
          type: 'linear',
          min: Date.now() - 24 * 3600 * 1000,
          max: Date.now(),
          ticks: {
            color: textColor,
            maxTicksLimit: 3,
            callback: (val) => {
              const diff = Date.now() - (val as number);
              const h24  = 24 * 3600 * 1000;
              const h12  = 12 * 3600 * 1000;
              if (diff < 5 * 60 * 1000)                    return 'Jetzt';
              if (Math.abs(diff - h12) < 30 * 60 * 1000)  return '–12h';
              if (Math.abs(diff - h24) < 30 * 60 * 1000)  return '–24h';
              return null;
            },
          },
          grid: { display: this.expanded, color: gridColor },
          title: { display: this.expanded, text: 'Zeit (24h)', color: textColor },
        },
        y: {
          display: true,
          ticks: {
            color: textColor,
            maxTicksLimit: this.expanded ? 6 : 3,
            callback: (val) => this.mode === 'humidity' ? `${val} %` : `${val} °`,
          },
          grid: { color: gridColor, display: this.expanded },
          title: {
            display: this.expanded,
            text: this.mode === 'humidity' ? 'Luftfeuchtigkeit (%)' : 'Temperatur (°C)',
            color: textColor,
          },
        },
      },
      elements: {
        point: { radius: 0, hoverRadius: 4 },
        line:  { borderWidth: this.expanded ? 2 : 1.5, tension: 0.6 },
      },
    };

    const isHumidity = this.mode === 'humidity';
    this.chartData = {
      datasets: [{
        data: this.points
          .filter(p => !isHumidity || p.humidity != null)
          .map(p => ({ x: p.ts, y: isHumidity ? (p.humidity as number) : p.tempC })),
        borderColor: isHumidity ? '#0dcaf0' : '#0d6efd',
        backgroundColor: 'transparent',
        fill: false,
      }],
    };
  }
}
